import { get, post } from "@/services/api.services";

export type UnsavedConfluenceExperimentPage = {
    title: string;
    content: string;
};

export type SavedConfluenceExperimentPage = UnsavedConfluenceExperimentPage & {
    confluenceUrl: string;
};

export type Version = {
    createdAt: string;
    message: string;
    number: number;
    minorEdit: boolean;
    authorId: string;
};

export type BasicPage = {
    id: string;
    status: "current";
    title: string;
    spaceId: string;
    parentId: string | null;
    parentType: string | null; // "page" | "folder"
    position: number;
    authorId: string;
    ownerId: string;
    lastOwnerId: string | null;
    createdAt: string;
    version: Version;
    body: {
        storage?: object;
        atlas_doc_format?: object;
        view?: object;
    };
    _links: {
        editui: string;
        edituiv2: string;
        tinyui: string;
        webui: string;
    };
};

export type Label = {
    id: string;
    name: string;
    prefix: string;
};

export type PageProperty = {
    id: string;
    key: string;
    version: object;
};

export type Operation = {
    operation: string;
    targetType: string;
};

export type ListMeta = {
    hasMore: boolean;
    cursor: string;
};

export type Like = { accountId: string };

export type Page = BasicPage & {
    labels: {
        results: Label[];
        meta: ListMeta;
        _links: { self: string };
    };
    properties: {
        results: PageProperty[];
        meta: ListMeta;
        _links: { self: string };
    };
    operations: {
        results: Operation[];
        meta: ListMeta;
        _links: { self: string };
    };
    likes: {
        results: Like[];
        meta: ListMeta;
        _links: { self: string };
    };
    versions: {
        results: Version[];
        meta: ListMeta;
        _links: { self: string };
    };
    isFavoritedByCurrentUser: boolean;
    _links: {
        base: string;
    };
};

export type ConfluenceFolder = {
    id: string;
    title: string;
};

export type BaseConfluenceTemplate = {
    templateId: string;
    name: string;
    description: string;
};

export type ConfluenceTemplate = BaseConfluenceTemplate & {
    body: {
        storage: {
            value: string;
        };
    };
};

export const confluenceAPI = {
    listFolders: async (parentFolderId: string): Promise<ConfluenceFolder[]> => {
        return get(`/confluence/folders/${parentFolderId}`);
    },
    findOrCreateExperimentGroupFolder: async (
        experimentGroupCode: string
    ): Promise<ConfluenceFolder> => {
        return post(`/confluence/folder/${experimentGroupCode}`);
    },
    getAllTemplates: async (): Promise<BaseConfluenceTemplate[]> => {
        return get("/confluence/templates");
    },
    getTemplateById: async (templateId: string): Promise<ConfluenceTemplate> => {
        return get(`/confluence/template/${templateId}`);
    },
    createTestPageFromTemplate: async (
        templateId: string
    ): Promise<SavedConfluenceExperimentPage> => {
        const response = await post(`/confluence/testPage/template/${templateId}`);
        return mapToSavedConfluenceExperimentPage(response);
    },
    fetchPageById: async (pageId: string): Promise<Page> => {
        return get(`/confluence/page/${pageId}`);
    },
    fetchNotebookEntryUrlForExperimentCode: async (
        experimentCode: string
    ): Promise<string | null> => {
        return get(`/confluence/notebookEntries/${experimentCode}`);
    },
    createNotebookEntryForExperimentCode: async (experimentCode: string): Promise<string> => {
        return post(`/confluence/notebookEntries/${experimentCode}`);
    },
};

export function mapToSavedConfluenceExperimentPage(data: any): SavedConfluenceExperimentPage {
    return {
        title: data.title,
        content: data.body.storage.value,
        confluenceUrl: data._links.base + data._links.webui,
    };
}
